import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Can from '../../auth/Can';
import DocumentFieldPicker from '../../components/workflow/DocumentFieldPicker';
import { listDocumentTypes, createDocumentType, updateDocumentType, toggleDocumentType } from '../../api/workflow';
import { pushToast } from '../../components/ui/toastBus';

function DocumentTypeFormModal({ open, onClose, documentType }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [fields, setFields] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    setName(documentType?.name || '');
    setFields(documentType?.verification_fields || []);
    setError('');
  }, [documentType]);

  const mutation = useMutation({
    mutationFn: () =>
      documentType ? updateDocumentType(documentType.id, { name: name.trim(), verification_fields: fields })
        : createDocumentType({ name: name.trim(), verification_fields: fields }),
    onSuccess: () => {
      pushToast({ type: 'success', message: documentType ? 'Document type updated.' : 'Document type created.' });
      queryClient.invalidateQueries({ queryKey: ['document-types'] });
      onClose();
    },
    onError: (err) =>
      setError(err.response?.data?.errors?.name?.[0] || err.response?.data?.message || 'Could not save document type.'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    mutation.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={documentType ? `Edit: ${documentType.name}` : 'New document type'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={mutation.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} error={error} />
        <DocumentFieldPicker value={fields} onChange={setFields} />
        {documentType && (
          <p className="text-xs text-gray-500">
            Code <span className="font-mono text-gray-700">{documentType.code}</span> — fixed once created, since workflow steps
            reference it.
          </p>
        )}
      </form>
    </Modal>
  );
}

export default function WorkflowDocuments() {
  const queryClient = useQueryClient();
  const [formTarget, setFormTarget] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const typesQuery = useQuery({ queryKey: ['document-types'], queryFn: listDocumentTypes });

  const toggleMutation = useMutation({
    mutationFn: toggleDocumentType,
    onSuccess: (row) => {
      pushToast({ type: 'success', message: row.is_active ? 'Document type activated.' : 'Document type deactivated.' });
      queryClient.invalidateQueries({ queryKey: ['document-types'] });
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not update document type.' }),
  });

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'code', label: 'Code', render: (row) => <span className="font-mono text-xs text-gray-500">{row.code}</span> },
    {
      key: 'verification_fields',
      label: 'Cross-check fields',
      render: (row) => <span className="text-gray-600">{row.verification_fields?.length || 0}</span>,
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (row) => <Badge tone={row.is_active ? 'green' : 'slate'}>{row.is_active ? 'Active' : 'Inactive'}</Badge>,
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="flex items-center gap-3 text-sm">
          <Can perm="workflow.documents.manage">
            <button
              onClick={() => {
                setFormTarget(row);
                setFormOpen(true);
              }}
              className="text-primary hover:underline"
            >
              Edit
            </button>
            <button
              onClick={() => toggleMutation.mutate(row.id)}
              disabled={toggleMutation.isPending}
              className={row.is_active ? 'text-gray-500 hover:underline' : 'text-green-600 hover:underline'}
            >
              {row.is_active ? 'Deactivate' : 'Activate'}
            </button>
          </Can>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Workflow Documents"
        description="Manage the document types available to workflow steps."
        actions={
          <Can perm="workflow.documents.manage">
            <Button
              onClick={() => {
                setFormTarget(null);
                setFormOpen(true);
              }}
            >
              New Document Type
            </Button>
          </Can>
        }
      />

      <Table
        columns={columns}
        data={typesQuery.data || []}
        isLoading={typesQuery.isLoading}
        emptyMessage="No document types yet."
      />

      {formOpen && <DocumentTypeFormModal open={formOpen} onClose={() => setFormOpen(false)} documentType={formTarget} />}
    </div>
  );
}
